/**
 * Global Unread Counts module.
 *
 * Tracks unread message counts from all linked personal accounts
 * configured in /etc/zulip/global.conf.
 *
 * Features:
 * 1. Real-time updates via Tornado events (global_unread_update)
 * 2. Cross-realm notification sounds when messages arrive on other realms
 * 3. Aggregated unread counts for the Inbox sidebar
 */

import * as channel from "./channel.ts";
import * as realm_switcher from "./realm_switcher.ts";
import * as unread_ui from "./unread_ui.ts";

interface GlobalUnreadUpdateEvent {
    type: "global_unread_update";
    source_user_id: number;
    source_realm_id: number;
    source_realm_name: string;
    unread_count: number;
    unread_mentions: number;
    notification_sound: string;
}

interface GlobalUnreadData {
    global_inbox_enabled: boolean;
    total_unread_count: number;
    total_unread_mentions: number;
    is_using_group_account: boolean;
}

// Cache for global unread data
let cached_data: GlobalUnreadData | null = null;
let is_enabled: boolean | null = null;
let initialized = false;
let baseline_initialized = false;

// Track unread counts per account (user_id -> unread_count)
// This is updated by Tornado events
const account_unreads: Map<number, number> = new Map();
const account_mentions: Map<number, number> = new Map();

// Queue for events that arrive before baseline is initialized
let pending_events: GlobalUnreadUpdateEvent[] = [];

export function get_cached_other_accounts_unread(): number {
    // Returns the cached total unread count from other linked accounts
    // (not including current account's unreads, which are counted separately)
    if (!cached_data || !cached_data.global_inbox_enabled || cached_data.is_using_group_account) {
        return 0;
    }
    return cached_data.total_unread_count;
}

export function get_cached_other_accounts_mentions(): number {
    // Returns the cached total mentions count from other linked accounts
    if (!cached_data || !cached_data.global_inbox_enabled || cached_data.is_using_group_account) {
        return 0;
    }
    return cached_data.total_unread_mentions;
}

/**
 * Play a notification sound for a specific realm.
 * 
 * Creates a temporary Audio element with the specified sound and plays it.
 * The Audio element is not added to the DOM and gets garbage collected.
 */
function play_notification_sound_for_realm(notification_sound: string): void {
    if (notification_sound === "none") {
        return;
    }
    
    // Create a temporary Audio element for cross-realm notifications
    // This allows playing a different sound than the current realm's setting
    // Using the Audio() constructor avoids DOM manipulation
    const audio_file = `/static/audio/notification_sounds/${notification_sound}`;
    const audio = new Audio(`${audio_file}.ogg`);
    
    // Fallback to mp3 if ogg fails
    audio.addEventListener("error", () => {
        const mp3_audio = new Audio(`${audio_file}.mp3`);
        void mp3_audio.play().catch(() => {
            // Ignore autoplay errors - browser may block without user interaction
        });
    });
    
    void audio.play().catch(() => {
        // Ignore autoplay errors - browser may block without user interaction
    });
}

/**
 * Internal function to process a global_unread_update event.
 * This should only be called after baseline is initialized.
 */
function process_global_unread_event(event: GlobalUnreadUpdateEvent): void {
    const {source_user_id, unread_count, unread_mentions, notification_sound} = event;
    
    // Get the previous count for comparison
    const previous_count = account_unreads.get(source_user_id) ?? 0;
    
    // Update the tracked counts for this account
    account_unreads.set(source_user_id, unread_count);
    account_mentions.set(source_user_id, unread_mentions);
    
    // Recalculate totals from all tracked accounts
    let total_unread = 0;
    let total_mentions = 0;
    for (const count of account_unreads.values()) {
        total_unread += count;
    }
    for (const mentions of account_mentions.values()) {
        total_mentions += mentions;
    }
    
    // Update cached data
    cached_data = {
        global_inbox_enabled: true,
        total_unread_count: total_unread,
        total_unread_mentions: total_mentions,
        is_using_group_account: false, // We don't get these events when on a group account
    };
    
    // Play notification sound if unread count increased
    if (unread_count > previous_count) {
        play_notification_sound_for_realm(notification_sound);
    }
    
    // Update the UI
    unread_ui.update_unread_counts();
    void realm_switcher.update();
}

/**
 * Handle a global_unread_update event from Tornado.
 * 
 * This is called when another linked account receives a message.
 * Updates the cached counts and plays the notification sound.
 */
export function handle_global_unread_update_event(event: GlobalUnreadUpdateEvent): void {
    // If baseline isn't initialized yet, queue the event and trigger initialization
    if (!baseline_initialized) {
        pending_events.push(event);
        // Try to initialize baseline now if we haven't started
        if (!initialized) {
            void initialize();
        }
        return;
    }
    
    process_global_unread_event(event);
}

export async function fetch_global_unread_counts(): Promise<GlobalUnreadData | null> {
    try {
        const response = await channel.get({
            url: "/json/global_inbox",
        });
        cached_data = response as GlobalUnreadData;
        return cached_data;
    } catch {
        return null;
    }
}

export async function check_global_inbox_enabled(): Promise<boolean> {
    if (is_enabled !== null) {
        return is_enabled;
    }
    try {
        const response = await channel.get({
            url: "/json/global_inbox/enabled",
        });
        is_enabled = (response as {global_inbox_enabled: boolean}).global_inbox_enabled;
        return is_enabled;
    } catch {
        is_enabled = false;
        return false;
    }
}

export function clear_cache(): void {
    cached_data = null;
    is_enabled = null;
    account_unreads.clear();
    account_mentions.clear();
    baseline_initialized = false;
    pending_events = [];
}

export async function initialize(): Promise<void> {
    if (initialized) {
        return;
    }
    initialized = true;

    // Check if global inbox is enabled for this user
    const enabled = await check_global_inbox_enabled();
    if (!enabled) {
        // Even if not enabled, mark baseline as initialized so events don't queue forever
        baseline_initialized = true;
        return;
    }

    // Fetch initial counts (to populate before any events arrive)
    await fetch_global_unread_counts();

    // IMPORTANT: Process pending events FIRST, before fetching API baseline.
    // This is because events that arrived during initialization represent
    // real new messages, and we want to play sounds for them. If we fetch
    // the API baseline first, it will already include these messages and
    // the comparison won't trigger sounds.
    
    // Mark baseline as initialized so process_pending_events works correctly
    baseline_initialized = true;
    
    // Process any events that arrived during initialization
    // These will use 0 as baseline (since account_unreads is empty),
    // which means sounds will play for any non-zero unread counts
    process_pending_events();
    
    // NOW fetch the API baseline to get the correct current state
    // This updates account_unreads with the final values
    await initialize_account_unreads_from_api();
}

/**
 * Process any events that were queued while waiting for baseline initialization.
 * 
 * These events represent real changes that happened while we were initializing.
 * Since account_unreads is empty when this is called, the baseline for each
 * account will be 0, which means sounds will play for any unread_count > 0.
 */
function process_pending_events(): void {
    const events = pending_events;
    pending_events = [];
    
    for (const event of events) {
        process_global_unread_event(event);
    }
}

/**
 * Initialize the account_unreads Map from the linked accounts API.
 * 
 * This ensures we have baseline unread counts before any Tornado events arrive.
 * Without this, we'd always treat the first event as a "new message" since
 * previous_count would be 0.
 */
async function initialize_account_unreads_from_api(): Promise<void> {
    try {
        const response = await channel.get({
            url: "/json/global_accounts",
        });
        const data = response as {
            accounts: Array<{
                user_id: number;
                unread_count: number;
                unread_mentions: number;
                is_current: boolean;
            }>;
        };

        // Populate the maps with initial values for all non-current accounts
        for (const account of data.accounts) {
            if (!account.is_current) {
                account_unreads.set(account.user_id, account.unread_count);
                account_mentions.set(account.user_id, account.unread_mentions);
            }
        }
    } catch {
        // Ignore errors - we'll just start with an empty baseline
    }
}

